import { graphql, type HeadProps, type PageProps } from "gatsby";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { useEffect, useMemo, type FC } from "react";
import { isValidIcon } from "../../components/general/Icon/Icon";
import { HeadMetaData } from "../../containers/headMetaData";
import { SEO } from "../../containers/seo";
import { useAppState } from "../../context/app/context";
import { useStoryblokPreviewData } from "../../hooks/use-storyblok-preview";
import type { GenericPageProps } from "../../type-definitions/pages";
import type { Mutable } from "../../type-definitions/utility-types";
import { showBabyListButton } from "../../utils/babylist";
import { generateCanonicalUrl } from "../../utils/canonicalUrl";
import {
	getHighestStockQuantity,
	isOnStock,
} from "../../utils/commercetools/availability";
import { pushCustomGTMEvent } from "../../utils/gtm/gtm-handlers";
import { getMaterials } from "../../utils/materials";
import { isInPreviewEditor } from "../../utils/storyblok/is-in-preview-editor";
import {
	getDefaultVariantForProductPage,
	getSlugForProductVariant,
	mergeVariants,
} from "../../utils/storyblok/product-variant";
import { getUrlFromStoryblokLink } from "../../utils/storyblok/urls";
import {
	findTranslatedSlugNameFromLinkForSite,
	findTranslatedSlugPathFromLinkForSite,
} from "../../utils/storyblok/utils";
import { makeWritable } from "../../utils/ts-utils";
import { notNull } from "../../utils/type-guards/general";
import ProductPage, { type ProductPageProps } from "./product-page.component";
import {
	filterByStore,
	type StoreItem,
} from "../../utils/storyblok/store-filters";

export type ProductPageContainerProps = GenericPageProps & {
	variantSlug: string;
	productPagePath: string;
};

const ProductPageContainer: FC<
	PageProps<Queries.ProductPageQuery, ProductPageContainerProps>
> = ({ data, pageContext }) => {
	const { t } = useTranslation();

	useEffect(() => {
		pushCustomGTMEvent({ type: "setPageType", data: { pageType: "product" } });
	}, []);

	const { variantSlug } = pageContext;

	const productPage: Mutable<typeof data.productPage> = data.productPage;

	const pageContent = useStoryblokPreviewData({
		...productPage,
		_uid: productPage?._uid ?? "",
		component: productPage?.component ?? "",
	});

	const product = makeWritable(data.productPage?.commercetools_product);

	if (product) {
		// Fallback when CT is missing the name but Storyblok has it
		product.name = product.name || pageContent?.name || "";
	}

	const allVariants = product
		? mergeVariants(
				product.masterVariant,
				product.variants as Mutable<typeof product.variants>,
			)
		: [];

	const defaultVariant =
		product &&
		product.masterVariant &&
		getDefaultVariantForProductPage(allVariants, product.masterVariant);

	const defaultVariantSlug = getSlugForProductVariant(defaultVariant);
	const variant =
		variantSlug === defaultVariantSlug || variantSlug === ""
			? defaultVariant
			: allVariants.find(
					(variant) =>
						variant &&
						variant.storyblok_variant_pages?.[0] &&
						variant.storyblok_variant_pages[0].full_slug === variantSlug,
				);

	const currentPageLink = findTranslatedSlugPathFromLinkForSite(
		data.breadcrumbs,
	);
	const currentPageName = findTranslatedSlugNameFromLinkForSite(
		data.breadcrumbs,
	);

	const parentPageLink = findTranslatedSlugPathFromLinkForSite(
		data.breadcrumbs?.parent_link?.parent_link,
	);
	const parentPageName = findTranslatedSlugNameFromLinkForSite(
		data.breadcrumbs?.parent_link?.parent_link,
	);

	const materials = product && variant ? getMaterials(product, variant) : [];

	const breadcrumbs =
		parentPageName && currentPageName
			? [
					{ link: "/", label: t("Home") },
					{
						link: parentPageLink,
						label: parentPageName,
					},
					{ link: currentPageLink, label: currentPageName },
				]
			: [];

	const backgroundColor = pageContent?.background_color ?? undefined;
	const accessories = isInPreviewEditor()
		? (pageContent?.accessories as
				| [Queries.StoryblokVariantProductFragment]
				| [])
		: (pageContent?.accessories_storyblok_pages as [
				Queries.StoryblokVariantProductFragment,
			]);

	const { siteConfig, userCountry } = useAppState();

	const tabs = useMemo(
		() =>
			(pageContent?.tabs ?? []).filter((tab) =>
				filterByStore(tab as StoreItem, siteConfig.store),
			) as Queries.ProductTabFragment[],
		[pageContent?.tabs, siteConfig.store],
	);

	const modules =
		pageContent?.modules && pageContent.modules.length > 0
			? pageContent.modules.map((module) => module as Mutable<typeof module>)
			: [];

	const productDetails = useMemo(
		() =>
			(pageContent?.product_details ?? []).filter((detail) =>
				filterByStore(detail as StoreItem, siteConfig.store),
			) as Queries.ProductDetailsFragment[],
		[pageContent?.product_details, siteConfig.store],
	);

	const shortDescriptions = useMemo(
		() =>
			(pageContent?.short_descriptions ?? []).filter((detail) =>
				filterByStore(detail as StoreItem, siteConfig.store),
			) as Queries.ShortDescriptionFragment[],
		[pageContent?.short_descriptions, siteConfig.store],
	);

	useEffect(() => {
		if (variant && product) {
			pushCustomGTMEvent({
				type: "productDetail",
				data: {
					variant,
					product,
					country: userCountry,
					currency: siteConfig.currency,
				},
			});
		}
	}, [product, siteConfig.currency, userCountry, variant]);

	const inStock = variant ? isOnStock(variant) : false;

	const highestStockQuantity = variant ? getHighestStockQuantity(variant) : 0;

	const shippingInfo = siteConfig.product_page_shipping_info;
	const deliveryInfo = siteConfig.product_page_delivery_info;
	const giftWrapInfo = siteConfig.product_page_gift_wrap_info;

	const badgeField = variant?.storyblok_variant_pages?.[0]?.field_label_string;
	const badge =
		badgeField === null || badgeField === undefined ? undefined : badgeField;

	const isGiftCard =
		productPage?.commercetools_product?.productType.obj.key === "gift-card";

	const buttons = pageContent?.buttons
		? pageContent.buttons
				.map((button) => button as Mutable<typeof button>)
				.filter(notNull)
		: [];

	const priceModuleButtons = buttons
		.filter(
			(button) => siteConfig.store && button.stores?.includes(siteConfig.store),
		)
		.map((button) => ({
			href: getUrlFromStoryblokLink(button.link),
			label: button.label ?? "",
			icon: isValidIcon(button.icon) ? button.icon : undefined,
			target: button.link?.target ?? "_self",
		}));

	if (!product || !variant || !pageContent) return null;

	const viewProps: ProductPageProps = {
		product,
		variant,
		modules,
		tabs,
		accessories,
		priceModuleButtons,
		productDetails,
		shortDescriptions,
		breadcrumbs,
		materials,
		inStock,
		badge,
		backgroundColor,
		stockQuantity: highestStockQuantity,
		isGiftCard,
		shippingInfo,
		deliveryInfo,
		giftWrapInfo,
		showBabyListButton: showBabyListButton(),
		showMyRegistryButton: ["en-CA"].includes(siteConfig.language),
	};
	return <ProductPage {...viewProps} />;
};

export const query = graphql`
	query ProductPage(
		$language: String!
		$storyblokUuid: String
		$storyblokLanguage: String!
	) {
		...Locales
		productPage: storyblokEntry(
			lang: { eq: $storyblokLanguage }
			field_component: { eq: "product_page" }
			uuid: { eq: $storyblokUuid }
		) {
			name
			id
			uuid
			_uid
			background_color: field_background_color_string
			component: field_component
			commercetools_product {
				...CommerceToolsProduct
			}
			...Modules
			...SeoMetaData
			tabs {
				...ProductTab
			}
			accessories
			accessories_storyblok_pages {
				...StoryblokVariantProduct
			}
			buttons {
				...LocalButton
			}
			product_details {
				...ProductDetails
			}
			short_descriptions {
				...ShortDescription
			}
		}

		breadcrumbs: storyblokLink(uuid: { eq: $storyblokUuid }) {
			real_path
			name
			alternates {
				path
				name
				lang
			}
			parent_link {
				parent_link {
					real_path
					name
					alternates {
						path
						name
						lang
					}
				}
			}
		}
	}
`;

export default ProductPageContainer;

export const Head: FC<
	HeadProps<Queries.ProductPageQuery, ProductPageContainerProps>
> = (props) => {
	const { siteConfig } = useAppState();
	const page: Mutable<typeof props.data.productPage> = props.data.productPage;
	const name =
		page?.meta_title ||
		page?.commercetools_product?.metaTitle ||
		page?.commercetools_product?.name ||
		page?.name;

	const description =
		page?.meta_description ||
		page?.commercetools_product?.metaDescription ||
		page?.commercetools_product?.description;

	const productPagePath = props.pageContext.productPagePath;

	const canonicalUrl =
		productPagePath === props.location.pathname
			? generateCanonicalUrl(siteConfig.name, props.location.pathname) // Self-referencing canonical URL for the master variant
			: `https://${siteConfig.name}${productPagePath}`; // Canonical URL for the product page for other variants

	return (
		<>
			<HeadMetaData />
			<SEO
				localesData={props.data.locales}
				title={name || ""}
				description={description || ""}
				noIndex={Boolean(page?.no_index)}
				noFollow={Boolean(page?.no_follow)}
				canonicalUrl={canonicalUrl}
			/>
		</>
	);
};
